import { useEffect, useState } from "react";
import { Download, Search, Eye, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { KitItemRow } from "@/components/KitPanel";

const STATUSES = ["Placed", "Shipped", "Delivered"];

const OrderDialog = ({ order, onClose }) => {
  const d = order?.delivery;
  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="admin-order-dialog">
        {order && (
          <>
            <DialogHeader><DialogTitle className="font-heading">{order.id}</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-500">{order.email}</p>
            <ul className="divide-y divide-slate-100">{order.items.map((i, k) => <KitItemRow key={k} item={i} />)}</ul>
            <div className="text-sm space-y-0.5 bg-slate-50 rounded-xl p-4">
              <p className="font-semibold">{d.full_name} · {d.phone}</p>
              <p>{d.address_line1}{d.address_line2 ? `, ${d.address_line2}` : ""}</p>
              <p>{d.city}, {d.state} {d.postal_code}, {d.country}</p>
              {d.notes && <p className="text-slate-500 italic pt-1">{d.notes}</p>}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const StatusSelect = ({ order, onChange }) => (
  <Select value={order.status} onValueChange={(v) => onChange(order.id, v)}>
    <SelectTrigger className="w-32 h-9" data-testid={`admin-order-status-select-${order.id}`}><SelectValue /></SelectTrigger>
    <SelectContent>
      {STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`admin-order-status-option-${s.toLowerCase()}`}>{s}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const OrdersTab = () => {
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState(null);
  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
  }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/orders/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const reset = async (o) => {
    if (!window.confirm(`Delete order ${o.id}? ${o.email} will be able to order again.`)) return;
    await api.delete(`/admin/orders/${o.id}`).catch((e) => toast.error(errMsg(e)));
    load();
  };

  const exportCsv = async () => {
    try {
      const { data } = await api.get("/admin/orders/export", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = Object.assign(document.createElement("a"), { href: url, download: "swag-orders.csv" });
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const list = orders.filter((o) => (filter === "all" || o.status === filter) && `${o.id} ${o.email} ${o.delivery.full_name}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by order, email, name" data-testid="admin-order-search-input"
            className="w-full h-10 pl-9 pr-4 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#4285F4]" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-36 h-10" data-testid="admin-order-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <button onClick={exportCsv} data-testid="admin-export-csv-btn" className="h-10 px-5 rounded-full bg-[#1A73E8] hover:bg-[#1557B0] text-white text-sm font-semibold flex items-center gap-2">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead><tr className="text-xs font-mono uppercase tracking-widest text-slate-400"><th className="pb-3">Order</th><th className="pb-3">Employee</th><th className="pb-3">Items</th><th className="pb-3">Placed</th><th className="pb-3">Status</th><th /></tr></thead>
          <tbody>
            {list.map((o) => (
              <tr key={o.id} className="border-t border-slate-100" data-testid={`admin-order-row-${o.id}`}>
                <td className="py-3 pr-4 font-mono text-xs">{o.id}</td>
                <td className="py-3 pr-4"><p className="font-medium">{o.delivery.full_name}</p><p className="text-xs text-slate-500">{o.email}</p></td>
                <td className="py-3 pr-4 text-slate-600 max-w-[260px]">{o.items.map((i) => [i.name, i.color, i.size].filter(Boolean).join(" ")).join(", ")}</td>
                <td className="py-3 pr-4 text-slate-500 whitespace-nowrap">{new Date(o.created_at).toLocaleDateString()}</td>
                <td className="py-3 pr-4"><StatusSelect order={o} onChange={setStatus} /></td>
                <td className="py-3 whitespace-nowrap text-right">
                  <button onClick={() => setView(o)} className="p-2 rounded-full text-slate-500 hover:bg-slate-100" data-testid={`admin-order-view-${o.id}`} aria-label="View"><Eye className="w-4 h-4" /></button>
                  <button onClick={() => reset(o)} className="p-2 rounded-full text-slate-400 hover:text-[#EA4335] hover:bg-red-50" data-testid={`admin-order-delete-${o.id}`} aria-label="Delete order"><RotateCcw className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!list.length && <p className="text-center text-sm text-slate-500 py-10" data-testid="admin-orders-empty">No orders yet.</p>}
      </div>
      <OrderDialog order={view} onClose={() => setView(null)} />
    </div>
  );
};
